import {
  Alert,
  Button,
  DatePicker,
  EmptyState,
  Select,
  Skeleton,
  Stack,
  Text,
  Title,
  TooltipIcon,
} from "@assetwatch/ui";
import { getRouteApi } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import type { CmeAsset, FeedbackVerdict } from "./api";
import { errorSubtext } from "./errorSubtext";
import { FeatureList } from "./FeatureList";
import { FeedbackItemDetails } from "./FeedbackItemDetails";
import { ItemRail } from "./ItemRail";
import {
  calendarDateFromParam,
  calendarDateParam,
  combineQueryCosts,
  type FeatureVerdicts,
  feedbackComplete,
  feedbackReadyToSave,
  groupAssetsByMachine,
  machineComment,
  machineLocked,
  pointVerdictFromFeatures,
  queryCostParts,
  railVerdict,
  savedFeatureVerdicts,
  selectedDay,
  shiftDateParam,
} from "./lib";
import { ObservationForm } from "./ObservationForm";
import { useCmeDay, useSaveCmeFeedback } from "./queries";
import { TrendChart, useTrendSeries } from "./TrendChart";
import type { RailItem } from "./types";

const routeApi = getRouteApi("/_app/cme-feedback");

type SavedFeedback = {
  verdict: FeedbackVerdict | null;
  comment: string;
};

export function CmeFeedbackScreen() {
  // The open date, CME, asset, and chart window live in the URL, so a link
  // shows anyone with access the same list. The CME is `cme_user_id`. The
  // name is only a dropdown label. Nothing is queried until a date is chosen.
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const window = selectedDay(search.date);

  const dayQuery = useCmeDay(window?.start, search.cme_user_id);
  const saveFeedback = useSaveCmeFeedback(window?.start);

  // A review saved from this page, ahead of the day query's refetch. The query
  // already carries anything stored in S3.
  const [saved, setSaved] = useState<Record<string, SavedFeedback>>({});
  // The machine unlocked with Edit. Saving it locks it again.
  const [editingMachine, setEditingMachine] = useState<string | null>(null);
  // Feature choices edited on this page. A point with no edits shows its saved
  // verdicts; the first edit starts from them, so a re-save keeps the rest.
  const [featureVerdicts, setFeatureVerdicts] = useState<FeatureVerdicts>({});

  function verdictsFor(item: CmeAsset): Record<string, FeedbackVerdict> {
    return featureVerdicts[item.id] ?? savedFeatureVerdicts(item);
  }

  function recordFeatureVerdict(
    assetId: string,
    featureId: string,
    verdict: FeedbackVerdict | null,
  ) {
    const asset = dayQuery.data?.items.find((item) => item.id === assetId);
    setFeatureVerdicts((current) => {
      const point = {
        ...(current[assetId] ?? (asset ? savedFeatureVerdicts(asset) : {})),
      };
      if (verdict === null) {
        delete point[featureId];
      } else {
        point[featureId] = verdict;
      }
      return { ...current, [assetId]: point };
    });
  }
  const dayItems: RailItem[] = (dayQuery.data?.items ?? []).map((item) => {
    const feedback = saved[item.id];
    const fromFeatures = pointVerdictFromFeatures(item, verdictsFor(item));
    const edited = featureVerdicts[item.id] !== undefined;
    // Saved on this page, or a review already stored in S3.
    const stored = feedback !== undefined || item.verdict !== null;
    const verdict = feedback
      ? railVerdict(fromFeatures, edited, feedback.verdict)
      : railVerdict(fromFeatures, edited, item.verdict ?? null);
    const comment = feedback ? feedback.comment : (item.comment ?? null);
    return {
      ...item,
      verdict,
      comment,
      reviewed: feedbackComplete({ stored, verdict, comment }),
    };
  });
  const serverCmes = dayQuery.data?.cmes ?? [];
  const namedCmes =
    serverCmes.length > 0
      ? serverCmes
      : dayItems.flatMap((item) =>
          item.cme_user_id !== null && item.cme_name.length > 0
            ? [{ cme_user_id: item.cme_user_id, cme_name: item.cme_name }]
            : [],
        );
  const linkedName = dayQuery.data?.cme_name;
  const dropdownCmes =
    search.cme_user_id !== undefined &&
    linkedName &&
    !namedCmes.some((cme) => cme.cme_user_id === search.cme_user_id)
      ? [
          ...namedCmes,
          { cme_user_id: search.cme_user_id, cme_name: linkedName },
        ]
      : namedCmes;
  const cmeOptions = [
    ...new Map(
      dropdownCmes.map((cme) => [
        cme.cme_user_id,
        { value: String(cme.cme_user_id), label: cme.cme_name },
      ]),
    ).values(),
  ].sort((left, right) => left.label.localeCompare(right.label));
  const waitingForCme =
    cmeOptions.length > 0 && search.cme_user_id === undefined;
  const items = waitingForCme
    ? []
    : dayItems.filter(
        (item) =>
          search.cme_user_id === undefined ||
          item.cme_user_id === search.cme_user_id,
      );
  const selected = items.find((item) => item.id === search.item);
  const selectedMachine = selected
    ? groupAssetsByMachine(items).find((group) =>
        group.items.some((item) => item.id === selected.id),
      )
    : undefined;
  const machinePoints = selected ? (selectedMachine?.items ?? [selected]) : [];
  const machineVerdicts: FeatureVerdicts = Object.fromEntries(
    machinePoints.map((point) => [point.id, verdictsFor(point)]),
  );
  // A machine is locked once every point has a stored review (from S3, or
  // saved on this page) until the CME clicks Edit. S3 versioning keeps each
  // earlier save, so a re-save after Edit doesn't lose history.
  const storedIds = new Set([
    ...Object.keys(saved),
    ...(dayQuery.data?.items ?? [])
      .filter((item) => item.verdict !== null)
      .map((item) => item.id),
  ]);
  const machineKey = selectedMachine?.key ?? selected?.id;
  const locked = machineLocked(
    machinePoints.map((point) => point.id),
    storedIds,
    machineKey,
    editingMachine,
  );
  // Editing feedback that's already saved can be cancelled: drop this
  // machine's unsaved rating changes (the saved ones show again) and re-lock.
  const editingSaved =
    !locked &&
    machineLocked(
      machinePoints.map((point) => point.id),
      storedIds,
      machineKey,
      null,
    );
  function cancelEdit() {
    setFeatureVerdicts((current) => {
      const next = { ...current };
      for (const point of machinePoints) {
        delete next[point.id];
      }
      return next;
    });
    saveFeedback.reset();
    setEditingMachine(null);
  }

  // The viewer's local day. yyyy-MM-dd strings compare in date order.
  const today = calendarDateParam(new Date());

  // A new day closes the open asset and drops any save error from the old one.
  function selectDate(date: string | undefined) {
    saveFeedback.reset();
    // A new day is a new list: an Edit from the old one must not carry over.
    setEditingMachine(null);
    navigate({
      search: { ...search, date, item: undefined, feature: undefined },
    });
  }

  // The save mutation is shared by every machine, so its error is cleared
  // whenever the form would show a different machine's points.
  // `feature` rides in the URL with the item, so a shared link opens the same
  // graph; opening an item from the list clears it (first feature).
  function openItem(id: string, feature?: string) {
    const nextMachine = groupAssetsByMachine(items).find((group) =>
      group.items.some((item) => item.id === id),
    );
    if (nextMachine?.key !== selectedMachine?.key) {
      saveFeedback.reset();
    }
    navigate({ search: { ...search, item: id, feature } });
  }

  return (
    <div
      // A white canvas with gray panels on it. It covers the app shell's gray
      // padding (negative margin) so this page reads as white edge to edge;
      // the shell itself, shared by every page, is unchanged.
      // Two rows: the search box beside the feedback pane's top boxes, so they
      // share one height; then the feedback items beside the graph.
      className="-mx-(--spacing-24) -mt-(--spacing-24) grid gap-(--spacing-16) bg-surface-container-lowest p-(--spacing-24)"
      style={{ gridTemplateColumns: "18rem minmax(0, 1fr)" }}
    >
      {/* One search area: the day, then the CME. */}
      <div className="col-start-1 row-start-1 rounded-(--radius-sm) border border-outline-subtle bg-surface-low p-(--spacing-16)">
        <Stack gap={16}>
          {/* The date field already reserves room below it for helper text. */}
          <Stack gap={0}>
            <DatePicker
              label="Date"
              onValueChange={(date) =>
                selectDate(date ? calendarDateParam(date) : undefined)
              }
              value={search.date ? calendarDateFromParam(search.date) : null}
            />
            {/* Most reviews are today or a day either side, so these skip the calendar. */}
            <div className="grid grid-cols-3 gap-(--spacing-8)">
              <Button
                disabled={!search.date}
                fullWidth
                onClick={() =>
                  search.date && selectDate(shiftDateParam(search.date, -1))
                }
                size="sm"
                variant="outline"
              >
                Prev day
              </Button>
              <Button
                disabled={search.date === today}
                fullWidth
                onClick={() => selectDate(today)}
                size="sm"
                variant="outline"
              >
                Today
              </Button>
              <Button
                disabled={!search.date || search.date >= today}
                fullWidth
                onClick={() =>
                  search.date && selectDate(shiftDateParam(search.date, 1))
                }
                size="sm"
                variant="outline"
              >
                Next day
              </Button>
            </div>
          </Stack>
          {window === null ? (
            <Text size="sm" tone="subtle">
              Choose a date to load CMEs.
            </Text>
          ) : dayQuery.isError ? (
            <Alert
              subtext={errorSubtext(dayQuery.error)}
              title="Couldn't load this date"
              tone="negative"
            />
          ) : dayQuery.isPending ? (
            <Text size="sm" tone="subtle">
              Loading this date…
            </Text>
          ) : (
            <Select
              label="CME"
              onChange={(value) => {
                // Clearing the search text (or backspacing past it)
                // reports no CME. Keep the current one until another
                // name is actually picked, so the list doesn't reset.
                const parsed = value ? Number(value) : Number.NaN;
                if (
                  !Number.isInteger(parsed) ||
                  parsed <= 0 ||
                  parsed === search.cme_user_id
                ) {
                  return;
                }
                saveFeedback.reset();
                // Same for a new CME: Edit doesn't carry over to their list.
                setEditingMachine(null);
                navigate({
                  search: {
                    ...search,
                    cme_user_id: parsed,
                    item: undefined,
                    feature: undefined,
                  },
                });
              }}
              options={cmeOptions}
              placeholder="Choose a CME"
              searchable
              reserveSubtextSpace={false}
              value={
                search.cme_user_id === undefined
                  ? null
                  : String(search.cme_user_id)
              }
            />
          )}
        </Stack>
      </div>
      {/*
        The feedback items get their own box. It takes the graph's height (its
        content is positioned out of the row's sizing), so the two bottoms line
        up and the items scroll inside rather than lengthening the page.
      */}
      <div className="relative col-start-1 row-start-2 min-h-[24rem] min-w-0">
        <div className="absolute inset-0 flex flex-col rounded-(--radius-sm) border border-outline-subtle bg-surface-low p-(--spacing-16)">
          {window === null || dayQuery.isError ? null : dayQuery.isPending ? (
            <Skeleton height={240} radius="md" width="100%" />
          ) : waitingForCme ? (
            <Text size="sm" tone="subtle">
              Choose a CME to see their feedback items.
            </Text>
          ) : (
            <ItemRail
              cost={combineQueryCosts([dayQuery.data?.cost])}
              items={items}
              onOpen={openItem}
              selectedId={search.item}
            />
          )}
        </div>
      </div>
      {window === null ? (
        <RightSide>
          <EmptyState
            description="Choose a date, then a CME, to see that day's flex slow trends."
            icon="message"
            title="Pick a date"
          />
        </RightSide>
      ) : waitingForCme ? (
        <RightSide>
          <EmptyState
            description="Choose a CME, then a feedback item, to see its feature trend."
            icon="message"
            title="Pick a CME"
          />
        </RightSide>
      ) : !search.item ? (
        <RightSide>
          <EmptyState
            description="Choose a feedback item on the left to see its feature trend, then record whether it was useful."
            icon="message"
            title="Pick a feedback item"
          />
        </RightSide>
      ) : !selected ? (
        <RightSide>
          <Alert
            subtext="This link names a feedback item that isn't in this CME's list for the selected date."
            title="Feedback item not found"
            tone="warning"
          />
        </RightSide>
      ) : (
        <FeedbackPane
          asset={selected}
          featureId={search.feature}
          form={
            <ObservationForm
              canSave={feedbackReadyToSave(machinePoints, {
                cme_name: selected.cme_name,
                machine: selected.machine,
              })}
              featureVerdicts={machineVerdicts}
              initialComment={machineComment(machinePoints)}
              key={machineKey}
              locked={locked}
              onCancel={editingSaved ? cancelEdit : undefined}
              onEdit={() => setEditingMachine(machineKey ?? null)}
              onSave={async (points) => {
                // Only a successful save marks the points reviewed in the rail.
                await saveFeedback.mutateAsync({
                  date: selected.detected_date,
                  cme_name: selected.cme_name,
                  machine_id: selected.machine_id,
                  machine: selected.machine,
                  points: points.map((point) => ({
                    monitoring_point_id: point.monitoring_point_id,
                    monitoring_point: point.monitoring_point,
                    verdict: point.verdict,
                    comment: point.comment,
                    features: point.features,
                  })),
                });
                setSaved((current) => {
                  const next = { ...current };
                  for (const point of points) {
                    next[point.id] = {
                      verdict: point.verdict,
                      comment: point.comment,
                    };
                  }
                  return next;
                });
                setEditingMachine(null);
              }}
              saveError={
                saveFeedback.isError ? errorSubtext(saveFeedback.error) : null
              }
              saving={saveFeedback.isPending}
              points={machinePoints}
            />
          }
          machinePoints={machinePoints}
          onSelectFeature={(pointId, featureId) => openItem(pointId, featureId)}
          locked={locked}
          onVerdict={recordFeatureVerdict}
          verdicts={machineVerdicts}
          windowEnd={window.end}
        />
      )}
    </div>
  );
}

/** The right column when no feedback item is open: one block over both rows. */
function RightSide({ children }: { children: ReactNode }) {
  return (
    <div className="col-start-2 row-span-2 row-start-1 min-w-0">{children}</div>
  );
}

/** One titled, bordered area of the feedback pane, so the three read as separate. */
function PaneSection({
  title,
  aside,
  children,
}: {
  title: string;
  /** Shown right after the title, e.g. an info icon. */
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex min-w-0 flex-col gap-(--spacing-12) rounded-(--radius-sm) border border-outline-subtle bg-surface-low p-(--spacing-16)">
      <div className="flex items-center gap-(--spacing-4)">
        <Title size="sm" weight="bold">
          {title}
        </Title>
        {aside}
      </div>
      {/* Content may grow to fill the box, so boxes in a row match heights cleanly. */}
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </section>
  );
}

/**
 * The open feedback item on one screen: details | features to rate | comment
 * and Save across the top, and the selected feature's graph below.
 */
function FeedbackPane({
  asset,
  featureId,
  machinePoints,
  locked,
  verdicts,
  form,
  windowEnd,
  onSelectFeature,
  onVerdict,
}: {
  asset: CmeAsset;
  featureId: string | undefined;
  machinePoints: RailItem[];
  /** Saved and not being edited: ratings are shown but can't change. */
  locked: boolean;
  verdicts: FeatureVerdicts;
  form: ReactNode;
  windowEnd: string;
  onSelectFeature: (pointId: string, featureId: string) => void;
  onVerdict: (
    pointId: string,
    featureId: string,
    verdict: FeedbackVerdict | null,
  ) => void;
}) {
  const series = useTrendSeries(asset, featureId, windowEnd);
  return (
    <>
      <div className="col-start-2 row-start-1 grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1.5fr)] gap-(--spacing-16)">
        <PaneSection
          aside={
            series.cost ? (
              <TooltipIcon
                description={`${queryCostParts(series.cost).join(" · ")}. Estimated Athena scan cost for this graph.`}
                label="Scan estimate"
                size="xs"
              />
            ) : null
          }
          title="Feedback item"
        >
          <FeedbackItemDetails
            asset={asset}
            predicted1x={series.predicted1x}
            rpmPending={series.rpmPending}
          />
        </PaneSection>
        <PaneSection title="Rate features">
          <FeatureList
            onSelect={onSelectFeature}
            locked={locked}
            onVerdict={onVerdict}
            points={machinePoints}
            selected={{ pointId: asset.id, featureId: series.stem?.id ?? "" }}
            verdicts={verdicts}
          />
        </PaneSection>
        <PaneSection title="Observation">{form}</PaneSection>
      </div>
      <div className="col-start-2 row-start-2 min-w-0">
        <TrendChart asset={asset} series={series} />
      </div>
    </>
  );
}
